import React, { useContext, useEffect, useRef, useState } from 'react';

import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow
} from '@mui/material';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import { useLocation, useNavigate } from 'react-router-dom';

import { Alerta } from '../../components/alerta';
import DadosUsuario from '../../utils/context';
import { downloadBlob } from '../../utils/csvExport';
import { formatCurrency } from '../../utils/stringFormat';
import { isPostSaleProfile } from '../../utils/userProfile';
import CadastrarLeadCRM from '../comercial/cadastrarLead';
import '../css/crmDashboard.css';
import '../css/crmLeads.css';
import {
    formatLeadColumnValue,
    getLeadPhaseToneClass,
    getLeadStatusToneClass,
    getSellerIdByEmail,
    LEAD_COLUMNS
} from './crmShared';
import { CrmLeadsColumnPicker, CrmLeadsFilters, CrmLeadsKanbanFilters, CrmLeadsViewToggle } from './crmLeadsFilters';
import { CrmLeadsKanbanBoard, useCrmLeadsKanban } from './crmLeadsKanban';
import {
    buildCrmLeadDateFilterPayload,
    countActiveCrmLeadFilters,
    CRM_LEAD_DEFAULT_VISIBLE_FIELDS,
    CRM_LEAD_MARKETING_FIELDS,
    CRM_LEAD_REQUIRED_COLUMN_FIELD,
    CRM_LEADS_INITIAL_DATE_FILTERS,
    CRM_LEADS_INITIAL_FILTERS,
    CRM_LEAD_DATE_FILTER_RANGES,
    CRM_LEAD_DEBOUNCED_FILTER_FIELDS,
    CRM_LEAD_INTEGER_FILTER_FIELDS,
    CRM_LEAD_SELECT_FILTER_FIELDS,
    CRM_LEAD_PHASE_LOCKED_HIDDEN_FIELDS,
    CRM_LEAD_TICKET_ORDER_FIELD,
    CRM_LEAD_WON_PHASE_ID,
    CRM_LEADS_VIEW_KANBAN,
    CRM_LEADS_VIEW_TABLE,
    fetchCrmLeadsPage,
    fetchCrmLeadsReport,
    getCrmLeadColumnFilterField,
    getCrmLeadTableOnlyFilterFields,
    isSameCrmLeadFilterValue,
    readCrmLeadColumnPreference,
    readCrmLeadViewPreference,
    saveCrmLeadColumnPreference,
    saveCrmLeadViewPreference
} from './crmLeadsData';

const renderLeadCellContent = (column, row) => {
    const value = formatLeadColumnValue(column, row[column.field]);

    if (column.field === 'status') {
        return (
            <span className={`ui-tone-pill crm-leads-status-pill ${getLeadStatusToneClass(row.status)}`}>
                {value}
            </span>
        );
    }

    if (column.field === 'fase') {
        return (
            <span className={`ui-tone-pill crm-leads-phase-pill ${getLeadPhaseToneClass(row.fase)}`}>
                {value}
            </span>
        );
    }

    if (column.field === 'nome') {
        return <span className='crm-leads-name-cell'>{value}</span>;
    }

    if (column.field === 'ticket') {
        return <span className='crm-leads-money-cell'>{value}</span>;
    }

    return value;
};

const getLeadPath = (row) => `/CRM/leads/${row.lead_id}`;

const openLeadInNewTab = (row) => window.open(getLeadPath(row), '_blank', 'noopener');

const hasTextSelection = () => Boolean(window.getSelection?.()?.toString());

const CrmLeads = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const initialListState = useRef(location.state?.leadListState ?? null);
    const { autorizacoes = [], email = '' } = useContext(DadosUsuario);
    const isPhaseLocked = isPostSaleProfile(autorizacoes);
    const canViewMarketingFilters = autorizacoes.includes('Adm') || autorizacoes.includes('MKT');
    const ownSellerId = getSellerIdByEmail(email);
    const defaultFilters = {
        ...CRM_LEADS_INITIAL_FILTERS,
        ...(ownSellerId !== null ? { vendedor_id: ownSellerId } : {}),
        ...(isPhaseLocked ? { fase_id: CRM_LEAD_WON_PHASE_ID } : {})
    };
    const selectableColumns = LEAD_COLUMNS.filter((column) => (
        !(isPhaseLocked && CRM_LEAD_PHASE_LOCKED_HIDDEN_FIELDS.includes(column.field)) &&
        (canViewMarketingFilters || !CRM_LEAD_MARKETING_FIELDS.includes(column.field))
    ));
    const [viewMode, setViewMode] = useState(() => (
        initialListState.current?.viewMode ?? readCrmLeadViewPreference(email) ?? CRM_LEADS_VIEW_TABLE
    ));
    const activeView = isPhaseLocked ? CRM_LEADS_VIEW_TABLE : viewMode;
    const isKanbanView = activeView === CRM_LEADS_VIEW_KANBAN;
    const [visibleFields, setVisibleFields] = useState(() => readCrmLeadColumnPreference(email) ?? CRM_LEAD_DEFAULT_VISIBLE_FIELDS);
    const visibleColumns = selectableColumns.filter((column) => (
        column.field === CRM_LEAD_REQUIRED_COLUMN_FIELD || visibleFields.includes(column.field)
    ));
    const tableWidth = visibleColumns.reduce((total, column) => total + column.width, 0);
    const initialFilters = useRef({
        ...defaultFilters,
        ...(initialListState.current?.filters ?? {}),
        ...(isPhaseLocked ? { fase_id: CRM_LEAD_WON_PHASE_ID } : {})
    });
    const [filters, setFilters] = useState(initialFilters.current);
    const [debouncedFilters, setDebouncedFilters] = useState(initialFilters.current);
    const [rows, setRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [totalTicket, setTotalTicket] = useState(null);
    const [page, setPage] = useState(initialListState.current?.page ?? 1);
    const [pageSize, setPageSize] = useState(initialListState.current?.pageSize ?? 50);
    const [dateFilters, setDateFilters] = useState(initialListState.current?.dateFilters ?? CRM_LEADS_INITIAL_DATE_FILTERS);
    const [isLoading, setIsLoading] = useState(true);
    const [isFetching, setIsFetching] = useState(false);
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [openCreateLead, setOpenCreateLead] = useState(false);
    const [leadListRefreshKey, setLeadListRefreshKey] = useState(0);
    const [message, setMessage] = useState('');
    const [statusAlerta, setStatusAlerta] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const hasMountedDebounce = useRef(false);
    const activeFilterCount = countActiveCrmLeadFilters(filters, dateFilters, defaultFilters);
    const isOwnLeadsView = ownSellerId !== null && filters.vendedor_id === ownSellerId;
    const tableOnlyFilterFields = getCrmLeadTableOnlyFilterFields(filters, dateFilters, defaultFilters);
    const showAlert = (text, severity = 'info') => { setMessage(text); setStatusAlerta(severity); setOpenAlerta(true); };
    const kanban = useCrmLeadsKanban({
        enabled: isKanbanView,
        filters: debouncedFilters,
        dateFilters,
        refreshKey: leadListRefreshKey,
        onError: (text) => showAlert(text, 'error')
    });

    useEffect(() => {
        if (!hasMountedDebounce.current) {
            hasMountedDebounce.current = true;
            return undefined;
        }

        const nextDebouncedFilters = CRM_LEAD_DEBOUNCED_FILTER_FIELDS.reduce((acc, field) => {
            acc[field] = filters[field];
            return acc;
        }, {});

        const timeoutId = window.setTimeout(() => {
            setDebouncedFilters((prev) => ({ ...prev, ...nextDebouncedFilters }));
        }, 1000);

        return () => window.clearTimeout(timeoutId);
    }, [filters.campaign, filters.cliente_id, filters.content, filters.cpf, filters.cupom, filters.email, filters.lead_id, filters.nome, filters.origem, filters.source, filters.telefone, filters.term]);

    useEffect(() => {
        if (isKanbanView) return undefined;
        let ignore = false;

        const loadLeads = async () => {
            setIsLoading(!hasLoaded);
            setIsFetching(hasLoaded);

            try {
                const data = await fetchCrmLeadsPage({
                    dateFilters: buildCrmLeadDateFilterPayload(dateFilters),
                    filters: debouncedFilters,
                    page,
                    pageSize
                });

                if (ignore) return;
                setRows(data.items);
                setTotalRows(data.total);
                setTotalTicket(data.totalTicket);
                setPage(data.page);
                setHasLoaded(true);
            } catch (error) {
                if (ignore) return;
                setMessage(error.message);
                setStatusAlerta('error');
                setOpenAlerta(true);
            } finally {
                if (!ignore) {
                    setIsLoading(false);
                    setIsFetching(false);
                }
            }
        };

        loadLeads();
        return () => { ignore = true; };
    }, [dateFilters, debouncedFilters, isKanbanView, leadListRefreshKey, page, pageSize]);

    const handleFilterChange = (field, value) => {
        setPage(1);
        const nextValue = Array.isArray(value)
            ? value.map(Number)
            : CRM_LEAD_INTEGER_FILTER_FIELDS.includes(field) || field === CRM_LEAD_TICKET_ORDER_FIELD
                ? (value === '' ? null : Number(value))
                : CRM_LEAD_SELECT_FILTER_FIELDS.includes(field) && value !== '' ? Number(value) : value;

        setFilters((prev) => ({ ...prev, [field]: nextValue }));

        if (CRM_LEAD_SELECT_FILTER_FIELDS.includes(field) || field === CRM_LEAD_TICKET_ORDER_FIELD) {
            setDebouncedFilters((prev) => ({ ...prev, [field]: nextValue }));
        }
    };

    const handleClearFilters = () => {
        setFilters(defaultFilters);
        setDebouncedFilters(defaultFilters);
        setDateFilters(CRM_LEADS_INITIAL_DATE_FILTERS);
        setPage(1);
    };

    const clearColumnFilters = (columnFields) => {
        const dateRanges = columnFields
            .map((field) => CRM_LEAD_DATE_FILTER_RANGES[field])
            .filter((range) => range && (dateFilters[range.start] || dateFilters[range.end]));
        const filterFields = columnFields
            .filter((field) => !CRM_LEAD_DATE_FILTER_RANGES[field])
            .map(getCrmLeadColumnFilterField)
            .filter((field) => field in defaultFilters && !isSameCrmLeadFilterValue(filters[field], defaultFilters[field]));

        if (dateRanges.length === 0 && filterFields.length === 0) return;

        if (filterFields.length > 0) {
            const resetValues = filterFields.reduce((acc, field) => ({ ...acc, [field]: defaultFilters[field] }), {});
            setFilters((prev) => ({ ...prev, ...resetValues }));
            setDebouncedFilters((prev) => ({ ...prev, ...resetValues }));
        }

        if (dateRanges.length > 0) {
            setDateFilters((prev) => dateRanges.reduce((acc, range) => ({
                ...acc,
                [range.start]: null,
                [range.end]: null
            }), prev));
        }

        setPage(1);
    };

    const applyVisibleFields = (nextFields, persistedFields) => {
        const hiddenFields = visibleColumns
            .map((column) => column.field)
            .filter((field) => field !== CRM_LEAD_REQUIRED_COLUMN_FIELD && !nextFields.includes(field));

        setVisibleFields(nextFields);
        saveCrmLeadColumnPreference(email, persistedFields);
        clearColumnFilters(hiddenFields);
    };

    const handleToggleColumn = (field) => {
        if (field === CRM_LEAD_REQUIRED_COLUMN_FIELD) return;
        const nextFields = visibleFields.includes(field)
            ? visibleFields.filter((visibleField) => visibleField !== field)
            : [...visibleFields, field];

        applyVisibleFields(nextFields, nextFields);
    };

    const handleRestoreDefaultColumns = () => applyVisibleFields(CRM_LEAD_DEFAULT_VISIBLE_FIELDS, null);

    const handleViewChange = (nextView) => {
        setViewMode(nextView);
        saveCrmLeadViewPreference(email, nextView);
    };

    const openLead = (row) => navigate(getLeadPath(row), {
        state: {
            leadData: row,
            leadListState: {
                filters,
                dateFilters,
                page,
                pageSize,
                viewMode: activeView
            }
        }
    });

    const handleRowClick = (event, row) => {
        if (hasTextSelection()) return;

        if (event.ctrlKey || event.metaKey) {
            openLeadInNewTab(row);
            return;
        }

        openLead(row);
    };

    const handleRowAuxClick = (event, row) => {
        if (event.button !== 1) return;
        event.preventDefault();
        openLeadInNewTab(row);
    };

    const handleRowKeyDown = (event, row) => {
        if (event.key !== 'Enter' || event.target !== event.currentTarget) return;
        event.preventDefault();
        openLead(row);
    };

    const handleDateRangeApply = (range, startDate, endDate) => {
        setDateFilters((prev) => ({
            ...prev,
            [range.start]: startDate,
            [range.end]: endDate
        }));
        setPage(1);
    };

    const handleExportCsv = async () => {
        setIsGeneratingReport(true);

        try {
            const response = await fetchCrmLeadsReport({
                dateFilters: buildCrmLeadDateFilterPayload(dateFilters),
                filters: debouncedFilters,
                page,
                pageSize
            });

            if (response instanceof Blob) {
                downloadBlob('leads.xlsx', response);
                showAlert('Relatório Excel gerado com sucesso.', 'success');
                return;
            }

            showAlert(response.data ?? response.message, response.status);
        } finally {
            setIsGeneratingReport(false);
        }
    };

    const handleCreateLeadSuccess = (response) => {
        setOpenCreateLead(false);
        setPage(1);
        setLeadListRefreshKey((prev) => prev + 1);
        showAlert(response.message, response.status);
    };

    return (
        <Box className='mainBox ui-workflow-page'>
            <Alerta texto={message} status={statusAlerta} open={openAlerta} handleClose={() => setOpenAlerta(false)} />

            <Backdrop open={isLoading && !isKanbanView} className='crm-leads-backdrop'>
                <CircularProgress color='inherit' />
                <p className='crm-leads-backdrop-text'>Carregando dados...</p>
            </Backdrop>

            <Dialog
                open={isGeneratingReport}
                disableEscapeKeyDown
                className='crm-leads-report-dialog'
                aria-labelledby='crm-leads-report-dialog-title'
            >
                <DialogContent className='crm-leads-report-dialog-content'>
                    <CircularProgress size={34} />
                    <div>
                        <strong id='crm-leads-report-dialog-title'>Gerando relatório</strong>
                        <span>Aguarde enquanto preparamos a planilha Excel.</span>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog
                open={openCreateLead}
                onClose={() => setOpenCreateLead(false)}
                maxWidth='md'
                fullWidth
                className='crm-leads-create-dialog'
            >
                <DialogContent className='crm-leads-create-dialog-content'>
                    <CadastrarLeadCRM
                        onCancel={() => setOpenCreateLead(false)}
                        onSuccess={handleCreateLeadSuccess}
                    />
                </DialogContent>
            </Dialog>

            <div className='crm-leads-shell'>
                {(hasLoaded || isKanbanView) && (
                    <Paper className='ui-workflow-workspace'>
                        <div className='crm-leads-table-toolbar'>
                            <div className='crm-leads-toolbar-heading'>
                                <h2 className='crm-leads-section-title'>{isOwnLeadsView ? 'Meus leads' : 'Base de leads'}</h2>
                                <div className='crm-leads-section-meta'>
                                    <p className='crm-leads-section-copy'>
                                        {isKanbanView
                                            ? (kanban.hasLoaded ? `${kanban.total} leads no funil` : 'Carregando funil...')
                                            : `${totalRows} leads encontrados`}
                                    </p>
                                    {activeFilterCount > 0 && (
                                        <button
                                            type='button'
                                            className='crm-leads-clear-all-button'
                                            onClick={handleClearFilters}
                                        >
                                            <FilterListRoundedIcon fontSize='inherit' />
                                            <span>Limpar {activeFilterCount} {activeFilterCount === 1 ? 'filtro' : 'filtros'}</span>
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className='crm-leads-toolbar-actions'>
                                {!isPhaseLocked && <CrmLeadsViewToggle value={activeView} onChange={handleViewChange} />}
                                <div className='crm-leads-actions'>
                                    <Button className='crm-leads-button crm-leads-button-secondary' startIcon={<DashboardRoundedIcon />} onClick={() => navigate('/CRM/dashboard')}>
                                        Dashboard
                                    </Button>
                                    <Button className='crm-leads-button crm-leads-button-primary' startIcon={<PersonAddAlt1RoundedIcon />} onClick={() => setOpenCreateLead(true)}>
                                        Novo Lead
                                    </Button>
                                    {!isKanbanView && (
                                        <CrmLeadsColumnPicker
                                            columns={selectableColumns}
                                            visibleFields={visibleFields}
                                            onToggleColumn={handleToggleColumn}
                                            onRestoreDefault={handleRestoreDefaultColumns}
                                        />
                                    )}
                                    <Button
                                        className='crm-leads-button crm-leads-button-secondary'
                                        startIcon={<DownloadRoundedIcon />}
                                        onClick={handleExportCsv}
                                        disabled={isGeneratingReport}
                                    >
                                        Exportar
                                    </Button>
                                </div>

                                {!isKanbanView && totalTicket !== null && (
                                    <div className='crm-leads-ticket-summary'>
                                        <span>Ticket total do filtro</span>
                                        <strong>{formatCurrency(totalTicket)}</strong>
                                    </div>
                                )}
                            </div>
                        </div>

                        {isKanbanView && (
                            <CrmLeadsKanbanFilters
                                filters={filters}
                                onFilterChange={handleFilterChange}
                                dateFilters={dateFilters}
                                onDateRangeApply={handleDateRangeApply}
                                tableFilterCount={tableOnlyFilterFields.length}
                                onClearTableFilters={() => clearColumnFilters(tableOnlyFilterFields)}
                            />
                        )}

                        <div className='crm-leads-progress-slot'>
                            {(isKanbanView ? kanban.isFetching : isFetching) && (
                                <LinearProgress className='crm-leads-progress' aria-label='Atualizando leads' />
                            )}
                        </div>

                        {isKanbanView && (
                            <CrmLeadsKanbanBoard
                                kanban={kanban}
                                filters={debouncedFilters}
                                dateFilters={dateFilters}
                                cardHandlers={{
                                    onClick: handleRowClick,
                                    onAuxClick: handleRowAuxClick,
                                    onKeyDown: handleRowKeyDown
                                }}
                            />
                        )}

                        {!isKanbanView && (
                            <TableContainer className='crm-leads-table-shell'>
                                <Table size='small' className='crm-leads-table' aria-label='Tabela de leads' aria-busy={isFetching} style={{ width: tableWidth }}>
                                    <TableHead className='crm-leads-table-head'>
                                        <TableRow>
                                            {visibleColumns.map((column) => (
                                                <TableCell
                                                    key={column.field}
                                                    className='crm-leads-head-cell'
                                                    data-field={column.field}
                                                    style={{ width: column.width }}
                                                >
                                                    {column.headerName}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                        <CrmLeadsFilters
                                            filters={filters}
                                            onFilterChange={handleFilterChange}
                                            dateFilters={dateFilters}
                                            canViewMarketingFilters={canViewMarketingFilters}
                                            columns={visibleColumns}
                                            onDateRangeApply={handleDateRangeApply}
                                        />
                                    </TableHead>

                                    <TableBody className={isFetching ? 'crm-leads-table-body is-fetching' : 'crm-leads-table-body'}>
                                        {rows.map((row, index) => (
                                            <TableRow
                                                key={`${row.lead_id}-${page}-${index}`}
                                                hover
                                                className='crm-leads-row'
                                                tabIndex={0}
                                                aria-label={`Abrir lead ${row.lead_id}`}
                                                onClick={(event) => handleRowClick(event, row)}
                                                onAuxClick={(event) => handleRowAuxClick(event, row)}
                                                onMouseDown={(event) => event.button === 1 && event.preventDefault()}
                                                onKeyDown={(event) => handleRowKeyDown(event, row)}
                                            >
                                                {visibleColumns.map((column) => (
                                                    <TableCell
                                                        key={column.field}
                                                        className='crm-leads-cell'
                                                        data-field={column.field}
                                                    >
                                                        {renderLeadCellContent(column, row)}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}

                                        {rows.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={visibleColumns.length} align='center' className='crm-leads-feedback-cell'>
                                                    Nenhum lead para os filtros aplicados.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {!isKanbanView && (
                            <div className='crm-leads-pagination-shell'>
                                <span className='crm-leads-pagination-count'>
                                    Página {page} de {Math.max(1, Math.ceil(totalRows / pageSize))}
                                </span>
                                <TablePagination
                                    component='div'
                                    count={totalRows}
                                    page={page - 1}
                                    onPageChange={(event, newPage) => setPage(newPage + 1)}
                                    rowsPerPage={pageSize}
                                    onRowsPerPageChange={(event) => {
                                        setPageSize(parseInt(event.target.value, 10));
                                        setPage(1);
                                    }}
                                    rowsPerPageOptions={[25, 50, 100]}
                                    labelRowsPerPage='Leads por página'
                                />
                            </div>
                        )}
                    </Paper>
                )}
            </div>
        </Box>
    );
};

export default CrmLeads;
